import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Flex, Heading, Text, Button, Badge, HStack, VStack, Card, CardBody, Divider,
  Textarea, Input, Select, FormControl, FormLabel, Avatar, Spinner, Link,
  Alert, AlertIcon, Progress, useColorModeValue, useToast, useDisclosure,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
} from '@chakra-ui/react';
import { ArrowLeft, Download, ExternalLink, ThumbsUp, ThumbsDown, Edit, Trash2, BookOpen, Eye } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/Dashboard/Sidebar';
import {
  getResource, updateResource, deleteResource, endorseResource, withdrawEndorsement, downloadResource,
} from '../api/resourceService';
import useResourceOptions from '../hooks/useResourceOptions';
import { useAuth } from '../context/AuthContext';
import { EndorsementCounts, StatusBadge, typeStyle } from '../components/Resources/ResourceBadges';
import ResourcePreview, { hasPreview } from '../components/Resources/ResourcePreview';
import { getSurah } from '../data/surahMeta';

const REVIEWER_ROLES = ['admin', 'expert', 'super_expert'];
const ROLE_LABELS = { admin: 'Admin', expert: 'Expert', super_expert: 'Verifier' };

// FastAPI returns a string for most errors and a list for validation errors
const errorMessage = (err) => {
  const detail = err.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join('; ');
  return detail || err.message;
};

const formatSize = (bytes) => (bytes ? `${(bytes / 1024 / 1024).toFixed(2)} MB` : null);

const ayahReference = (r) => {
  if (!r.surah_id) return null;
  const surah = getSurah(r.surah_id);
  const name = surah ? `Surah ${surah.name}` : `Surah ${r.surah_id}`;
  if (!r.ayah_start) return name;
  if (!r.ayah_end || r.ayah_end === r.ayah_start) return `${name}, ayah ${r.ayah_start}`;
  return `${name}, ayahs ${r.ayah_start}-${r.ayah_end}`;
};


// ============ ENDORSEMENT PANEL ============

const EndorsementPanel = ({ resource, options, canVote, onChange }) => {
  const toast = useToast();
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const subtextColor = useColorModeValue('gray.600', 'gray.400');

  const required = options.endorsements_required;
  const progress = Math.min(100, ((resource.endorse_count || 0) / required) * 100);

  const vote = async (choice) => {
    if (choice === 'not_endorse' && !comment.trim()) {
      toast({ title: 'Please say why you do not endorse it', status: 'warning', duration: 4000 });
      return;
    }
    setBusy(true);
    try {
      onChange(await endorseResource(resource.id, choice, comment.trim() || null));
      setComment('');
      toast({ title: 'Your vote was saved', status: 'success', duration: 2500 });
    } catch (err) {
      toast({ title: 'Vote failed', description: errorMessage(err), status: 'error', duration: 5000 });
    }
    setBusy(false);
  };

  const withdraw = async () => {
    setBusy(true);
    try {
      onChange(await withdrawEndorsement(resource.id));
      toast({ title: 'Your vote was withdrawn', status: 'info', duration: 2500 });
    } catch (err) {
      toast({ title: 'Could not withdraw', description: errorMessage(err), status: 'error', duration: 5000 });
    }
    setBusy(false);
  };

  return (
    <VStack align="stretch" spacing={4}>
      <Flex justify="space-between" align="center">
        <Heading size="sm">Expert endorsement</Heading>
        <EndorsementCounts resource={resource} size="lg" />
      </Flex>

      {resource.status === 'pending' && (
        <Box>
          <Progress value={progress} size="sm" colorScheme="green" borderRadius="full" />
          <Text fontSize="xs" color={subtextColor} mt={1}>
            {resource.endorse_count || 0} of {required} endorsements needed to publish
          </Text>
        </Box>
      )}

      {canVote && (
        <Box>
          {resource.my_vote && (
            <Text fontSize="sm" mb={2}>
              You voted <strong>{resource.my_vote === 'endorse' ? 'Endorse' : 'Not endorse'}</strong>.
              You can change your vote or withdraw it.
            </Text>
          )}
          <Textarea
            size="sm" rows={3} value={comment} maxLength={2000}
            placeholder="Comment (required if you do not endorse)"
            onChange={(e) => setComment(e.target.value)}
            mb={2}
          />
          <HStack>
            <Button
              flex={1} colorScheme="green" leftIcon={<ThumbsUp size={16} />} isLoading={busy}
              variant={resource.my_vote === 'endorse' ? 'solid' : 'outline'}
              onClick={() => vote('endorse')}
            >
              Endorse
            </Button>
            <Button
              flex={1} colorScheme="red" leftIcon={<ThumbsDown size={16} />} isLoading={busy}
              variant={resource.my_vote === 'not_endorse' ? 'solid' : 'outline'}
              onClick={() => vote('not_endorse')}
            >
              Not endorse
            </Button>
          </HStack>
          {resource.my_vote && (
            <Button size="xs" variant="link" mt={2} onClick={withdraw} isDisabled={busy}>
              Withdraw my vote
            </Button>
          )}
        </Box>
      )}

      <Divider />

      {resource.endorsements?.length ? (
        <VStack align="stretch" spacing={3}>
          {resource.endorsements.map((e) => (
            <HStack key={e.voter_id} align="flex-start" spacing={3}>
              <Avatar size="xs" name={e.voter_name} />
              <Box flex={1}>
                <HStack spacing={2} flexWrap="wrap">
                  <Text fontSize="sm" fontWeight="medium">{e.voter_name}</Text>
                  <Badge fontSize="2xs">{ROLE_LABELS[e.voter_role] || e.voter_role}</Badge>
                  <Badge colorScheme={e.vote === 'endorse' ? 'green' : 'red'} fontSize="2xs">
                    {e.vote === 'endorse' ? 'Endorsed' : 'Not endorsed'}
                  </Badge>
                </HStack>
                {e.comment && <Text fontSize="sm" color={subtextColor} mt={1}>{e.comment}</Text>}
              </Box>
            </HStack>
          ))}
        </VStack>
      ) : (
        <Text fontSize="sm" color={subtextColor}>No expert has voted yet.</Text>
      )}
    </VStack>
  );
};


// ============ EDIT MODAL ============

const EditModal = ({ resource, options, isOpen, onClose, onSaved }) => {
  const toast = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm({
        title: resource.title || '',
        description: resource.description || '',
        usage_guide: resource.usage_guide || '',
        knowledge_type: resource.knowledge_type || '',
        tags: (resource.tags || []).join(', '),
        license: resource.license || '',
      });
    }
  }, [isOpen, resource]);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateResource(resource.id, {
        ...form,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      });
      onSaved(updated);
      onClose();
      toast({ title: 'Resource updated', status: 'success', duration: 2500 });
    } catch (err) {
      toast({ title: 'Update failed', description: errorMessage(err), status: 'error', duration: 5000 });
    }
    setSaving(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit resource</ModalHeader>
        <ModalCloseButton />
        {form && (
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Title</FormLabel>
                <Input value={form.title} maxLength={200} onChange={(e) => handleChange('title', e.target.value)} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Knowledge type</FormLabel>
                <Select value={form.knowledge_type} onChange={(e) => handleChange('knowledge_type', e.target.value)}>
                  {options.knowledge_types.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Description</FormLabel>
                <Textarea rows={3} value={form.description} onChange={(e) => handleChange('description', e.target.value)} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>How people can use this</FormLabel>
                <Textarea rows={4} value={form.usage_guide} onChange={(e) => handleChange('usage_guide', e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>Tags</FormLabel>
                <Input value={form.tags} placeholder="comma, separated" onChange={(e) => handleChange('tags', e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>Licence or citation</FormLabel>
                <Input value={form.license} maxLength={200} onChange={(e) => handleChange('license', e.target.value)} />
              </FormControl>
              <Text fontSize="sm" color="gray.500" alignSelf="start">
                To change the file or link itself, submit a new resource; experts endorsed this one as it is.
              </Text>
            </VStack>
          </ModalBody>
        )}
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button colorScheme="teal" onClick={save} isLoading={saving}>Save</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};


// ============ MAIN PAGE ============

const ResourceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const options = useResourceOptions();
  const editModal = useDisclosure();
  const deleteModal = useDisclosure();

  const [resource, setResource] = useState(null);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const subtextColor = useColorModeValue('gray.600', 'gray.400');
  const guideBg = useColorModeValue('teal.50', 'teal.900');

  const load = useCallback(async () => {
    try {
      setResource(await getResource(id));
    } catch (err) {
      setError(err.response?.status === 404 ? 'This resource does not exist or is not published yet.' : errorMessage(err));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const isOwner = user && resource && user.userId === resource.submitted_by;
  const canManage = isOwner || user?.role === 'admin';
  const canVote = REVIEWER_ROLES.includes(user?.role) && !isOwner;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadResource(resource.id, resource.filename);
      setResource((prev) => ({ ...prev, download_count: (prev.download_count || 0) + 1 }));
    } catch (err) {
      toast({ title: 'Download failed', description: errorMessage(err), status: 'error', duration: 5000 });
    }
    setDownloading(false);
  };

  const handleDelete = async () => {
    try {
      await deleteResource(resource.id);
      toast({ title: 'Resource deleted', status: 'success', duration: 2500 });
      navigate('/resources?view=mine');
    } catch (err) {
      toast({ title: 'Delete failed', description: errorMessage(err), status: 'error', duration: 5000 });
    }
  };

  const renderBody = () => {
    if (error) {
      return (
        <Alert status="warning" borderRadius="lg"><AlertIcon />{error}</Alert>
      );
    }
    if (!resource) {
      return <Flex justify="center" py={20}><Spinner size="xl" /></Flex>;
    }

    const { icon: Icon, color } = typeStyle(resource.resource_type);
    const typeInfo = options.resourceType(resource.resource_type);
    const reference = ayahReference(resource);
    const details = [
      ['Resource type', typeInfo?.label || resource.resource_type],
      ['Knowledge type', options.knowledgeLabel(resource.knowledge_type)],
      ['Quran reference', reference],
      ['Reciter', resource.reciter_name],
      ['File', resource.filename && `${resource.filename} (${formatSize(resource.file_size)})`],
      ['Downloads', resource.kind === 'file' ? String(resource.download_count || 0) : null],
      ['Licence / citation', resource.license],
      ['Shared by', resource.submitted_by_name],
      ['Shared on', resource.created_at && new Date(resource.created_at).toLocaleDateString()],
    ].filter(([, value]) => value);

    return (
      <Flex gap={6} direction={{ base: 'column', lg: 'row' }} align="flex-start">
        {/* Main column */}
        <VStack flex={2} align="stretch" spacing={6} w="full">
          <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
            <CardBody>
              <HStack spacing={4} align="flex-start" mb={4}>
                <Flex
                  w="56px" h="56px" borderRadius="xl" align="center" justify="center" flexShrink={0}
                  bg={`${color}.50`} color={`${color}.500`}
                >
                  <Icon size={28} />
                </Flex>
                <Box flex={1}>
                  <HStack spacing={2} mb={1} flexWrap="wrap">
                    <StatusBadge status={resource.status} />
                    <Badge colorScheme={color} variant="subtle">{typeInfo?.label || resource.resource_type}</Badge>
                    <Badge variant="subtle">{options.knowledgeLabel(resource.knowledge_type)}</Badge>
                  </HStack>
                  <Heading size="lg">{resource.title}</Heading>
                </Box>
              </HStack>

              <Text color={subtextColor} whiteSpace="pre-wrap" mb={4}>{resource.description}</Text>

              <HStack spacing={3} flexWrap="wrap">
                {resource.kind === 'file' ? (
                  <Button colorScheme="teal" leftIcon={<Download size={16} />} onClick={handleDownload} isLoading={downloading}>
                    Download
                  </Button>
                ) : (
                  <Button
                    as="a" href={resource.url} target="_blank" rel="noopener noreferrer"
                    colorScheme="teal" leftIcon={<ExternalLink size={16} />}
                  >
                    Open link
                  </Button>
                )}
                {canManage && (
                  <>
                    <Button variant="outline" leftIcon={<Edit size={16} />} onClick={editModal.onOpen}>Edit</Button>
                    <Button variant="outline" colorScheme="red" leftIcon={<Trash2 size={16} />} onClick={deleteModal.onOpen}>
                      Delete
                    </Button>
                  </>
                )}
              </HStack>
              {resource.kind === 'link' && (
                <Link href={resource.url} isExternal fontSize="xs" color={subtextColor} mt={2} display="block" wordBreak="break-all">
                  {resource.url}
                </Link>
              )}
            </CardBody>
          </Card>

          {hasPreview(resource) && (
            <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
              <CardBody>
                <HStack mb={3}>
                  <Eye size={20} />
                  <Heading size="md">Preview</Heading>
                </HStack>
                <ResourcePreview resource={resource} />
              </CardBody>
            </Card>
          )}

          <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
            <CardBody>
              <HStack mb={3}>
                <BookOpen size={20} />
                <Heading size="md">How people can use this</Heading>
              </HStack>
              <Box bg={guideBg} p={4} borderRadius="lg">
                <Text whiteSpace="pre-wrap">
                  {resource.usage_guide || 'The submitter has not described how to use this resource yet.'}
                </Text>
              </Box>
              {resource.tags?.length > 0 && (
                <HStack mt={4} spacing={2} flexWrap="wrap">
                  {resource.tags.map((tag) => <Badge key={tag} variant="outline">{tag}</Badge>)}
                </HStack>
              )}
            </CardBody>
          </Card>
        </VStack>

        {/* Side column */}
        <VStack flex={1} align="stretch" spacing={6} w="full" minW={{ lg: '320px' }}>
          <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
            <CardBody>
              <EndorsementPanel resource={resource} options={options} canVote={canVote} onChange={setResource} />
            </CardBody>
          </Card>

          <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
            <CardBody>
              <Heading size="sm" mb={3}>Details</Heading>
              <VStack align="stretch" spacing={2}>
                {details.map(([label, value]) => (
                  <Flex key={label} justify="space-between" gap={4} fontSize="sm">
                    <Text color={subtextColor}>{label}</Text>
                    <Text textAlign="right" wordBreak="break-word">{value}</Text>
                  </Flex>
                ))}
              </VStack>
            </CardBody>
          </Card>
        </VStack>

        <EditModal
          resource={resource} options={options}
          isOpen={editModal.isOpen} onClose={editModal.onClose} onSaved={setResource}
        />
        <Modal isOpen={deleteModal.isOpen} onClose={deleteModal.onClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Delete resource</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              Delete <strong>{resource.title}</strong> and all its expert votes? This cannot be undone.
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={deleteModal.onClose}>Cancel</Button>
              <Button colorScheme="red" onClick={handleDelete}>Delete</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Flex>
    );
  };

  return (
    <Flex direction="row" minH="100vh" bg={pageBg}>
      <Sidebar isCollapsed={sidebarCollapsed} toggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <Box
        pl={sidebarCollapsed ? '60px' : '250px'}
        w="full" overflowY="auto" pb={8}
        transition="padding-left 0.2s ease"
      >
        <Box px={8} py={6} maxW="1200px">
          <Button variant="ghost" leftIcon={<ArrowLeft size={16} />} mb={4} onClick={() => navigate('/resources')}>
            All resources
          </Button>
          {renderBody()}
        </Box>
      </Box>
    </Flex>
  );
};

export default ResourceDetail;
